import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  AnalyticsFiltersOptions,
  AnalyticsFiltersState,
  DateRangeValue,
} from "./types";
import { fetchAnalyticsFilters } from "./api";

type PartialFilters = Partial<AnalyticsFiltersState>;

const getTimestamp = (value: string | Date | null) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

const areStatesEqual = (a: AnalyticsFiltersState, b: AnalyticsFiltersState) => {
  return (
    a.clienteIntermedioId === b.clienteIntermedioId &&
    a.clienteFinalId === b.clienteFinalId &&
    a.programaId === b.programaId &&
    a.actividadId === b.actividadId &&
    getTimestamp(a.dateRange.from) === getTimestamp(b.dateRange.from) &&
    getTimestamp(a.dateRange.to) === getTimestamp(b.dateRange.to)
  );
};

const mergeState = (
  base: AnalyticsFiltersState,
  initialState?: PartialFilters,
  lockedFilters?: PartialFilters,
) => {
  const next: AnalyticsFiltersState = {
    ...base,
    ...(initialState ?? {}),
  };

  if (lockedFilters) {
    const mutableNext = next as unknown as Record<string, unknown>;
    Object.entries(lockedFilters).forEach(([key, value]) => {
      if (value !== undefined) {
        mutableNext[key] = value as unknown;
      }
    });
  }
  return next;
};

const defaultState: AnalyticsFiltersState = {
  clienteIntermedioId: null,
  clienteFinalId: null,
  programaId: null,
  actividadId: null,
  dateRange: { from: null, to: null },
};

type AnalyticsFiltersContextValue = {
  state: AnalyticsFiltersState;
  setFilter: <K extends keyof AnalyticsFiltersState>(
    key: K,
    value: AnalyticsFiltersState[K],
  ) => void;
  resetFilters: () => void;
  options: AnalyticsFiltersOptions | null;
  derivedOptions: {
    clientesFinales: AnalyticsFiltersOptions["clientesFinales"];
    programas: AnalyticsFiltersOptions["programas"];
    actividades: AnalyticsFiltersOptions["actividades"];
  };
  loading: boolean;
  error: string | null;
  lockedFilters: PartialFilters;
};

const AnalyticsFiltersContext =
  createContext<AnalyticsFiltersContextValue | null>(null);

type AnalyticsFiltersProviderProps = {
  children: React.ReactNode;
  initialState?: PartialFilters;
  lockedFilters?: PartialFilters;
};

export const AnalyticsFiltersProvider = ({
  children,
  initialState,
  lockedFilters,
}: AnalyticsFiltersProviderProps) => {
  const [baseState, setBaseState] = useState<AnalyticsFiltersState>(() =>
    mergeState(defaultState, initialState, lockedFilters),
  );
  const [state, setState] = useState<AnalyticsFiltersState>(baseState);
  const [options, setOptions] = useState<AnalyticsFiltersOptions | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const nextBase = mergeState(defaultState, initialState, lockedFilters);
    setBaseState((prev) => (areStatesEqual(prev, nextBase) ? prev : nextBase));
  }, [initialState, lockedFilters]);

  useEffect(() => {
    setState((prev) => (areStatesEqual(prev, baseState) ? prev : baseState));
  }, [baseState]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchAnalyticsFilters()
      .then((data) => {
        if (!active) return;
        setOptions(data);
        setError(null);
      })
      .catch((err) => {
        console.error("Error cargando filtros analytics", err);
        if (!active) return;
        setError("No se pudieron cargar los filtros");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const derivedOptions = useMemo(() => {
    if (!options) {
      return { clientesFinales: [], programas: [], actividades: [] };
    }
    const finales = options.clientesFinales.filter((cf) =>
      state.clienteIntermedioId
        ? cf.clienteIntermedioId === state.clienteIntermedioId
        : true,
    );
    const programas = options.programas.filter((programa) =>
      state.clienteFinalId ? programa.clienteFinalId === state.clienteFinalId : true,
    );
    const actividades = options.actividades.filter((actividad) =>
      state.programaId ? actividad.programaId === state.programaId : true,
    );
    return { clientesFinales: finales, programas, actividades };
  }, [options, state.clienteIntermedioId, state.clienteFinalId, state.programaId]);

  const lockedKeys = useMemo(() => {
    if (!lockedFilters) return new Set<keyof AnalyticsFiltersState>();
    return new Set(
      Object.entries(lockedFilters)
        .filter(([, value]) => value !== undefined)
        .map(([key]) => key as keyof AnalyticsFiltersState),
    );
  }, [lockedFilters]);

  const setFilter = useCallback(
    <K extends keyof AnalyticsFiltersState>(
      key: K,
      value: AnalyticsFiltersState[K],
    ) => {
      setState((prev) => {
        if (lockedKeys.has(key)) {
          return prev;
        }
        const next = { ...prev, [key]: value };
        // Cascada: si cambia un filtro padre, resetear hijos dependientes
        if (key === "clienteIntermedioId") {
          next.clienteFinalId = null;
          next.programaId = null;
          next.actividadId = null;
        } else if (key === "clienteFinalId") {
          next.programaId = null;
          next.actividadId = null;
        } else if (key === "programaId") {
          next.actividadId = null;
        } else if (key === "dateRange") {
          const range = value as DateRangeValue;
          next.dateRange = range;
        }
        return next;
      });
    },
    [lockedKeys],
  );

  const resetFilters = useCallback(() => {
    setState(baseState);
  }, [baseState]);

  const ctxValue = useMemo<AnalyticsFiltersContextValue>(
    () => ({
      state,
      setFilter,
      resetFilters,
      options,
      derivedOptions,
      loading,
      error,
      lockedFilters: lockedFilters ?? {},
    }),
    [
      state,
      setFilter,
      resetFilters,
      options,
      derivedOptions,
      loading,
      error,
      lockedFilters,
    ],
  );

  return (
    <AnalyticsFiltersContext.Provider value={ctxValue}>
      {children}
    </AnalyticsFiltersContext.Provider>
  );
};

export const useAnalyticsFilters = () => {
  const ctx = useContext(AnalyticsFiltersContext);
  if (!ctx) {
    throw new Error(
      "useAnalyticsFilters debe usarse dentro de un AnalyticsFiltersProvider",
    );
  }
  return ctx;
};
